import { Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { authService } from "@/services/auth";
import { MfaVerificationDialog } from "@/components/auth/MfaVerificationDialog";

import { paths } from "../../routes/paths";
import { useAuthContext } from "../../hooks/use-auth-context";
import { useRouter, useSearchParams } from "../../routes/hooks";

const INITIAL_WAIT = 2000;
const CHECK_INTERVAL = 500;
const MAX_ATTEMPTS = 10;

export default function RegistrationConfirmation() {
  const router = useRouter();
  const { user, checkUserSession } = useAuthContext();
  const searchParams = useSearchParams();
  const [attempts, setAttempts] = useState(0);
  const [isNewUser, setIsNewUser] = useState<boolean | null>(null);
  const [showMfaDialog, setShowMfaDialog] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState("");
  const [mfaChallengeId, setMfaChallengeId] = useState("");
  const [checkingMfa, setCheckingMfa] = useState(false);
  const returnTo = searchParams.get("returnTo");
  const token = searchParams.get("token");

  // Handle successful MFA verification
  const handleMfaSuccess = async () => {
    setShowMfaDialog(false);
    await checkUserSession?.();

    // Mark this MFA session as verified
    if (window && (window as any).setMfaVerifiedForSession) {
      (window as any).setMfaVerifiedForSession();
    }

    // Check if the user has completed onboarding after session refresh
    const onboardingCompleted =
      user?.user_metadata?.onboarding_completed === true;

    // If they haven't completed onboarding, redirect to onboarding first
    if (!onboardingCompleted) {
      router.push(paths.auth.onboarding);
      return;
    }

    // Otherwise proceed with normal flow
    const redirectUrl = token ? `${returnTo}?token=${token}` : returnTo;
    router.push(redirectUrl as string);
  };

  // Handle MFA verification error
  const handleMfaError = (error: Error) => {
    console.error("MFA verification error:", error);
    // Don't proceed with login on verification error
    // Keep showing the MFA dialog so user can retry
    // The dialog itself will show an error message
  };

  // Complete the login flow without MFA
  const completeLoginFlow = () => {
    if (!user?.id) return;

    sessionStorage.setItem(`user_registered_${user.id}`, "true");

    // Check if the user has completed onboarding
    const onboardingCompleted =
      user.user_metadata?.onboarding_completed === true;

    // If they haven't completed onboarding, redirect to onboarding first
    if (!onboardingCompleted) {
      router.push(paths.auth.onboarding);
      return;
    }

    // Otherwise proceed with normal flow
    const redirectUrl = token ? `${returnTo}?token=${token}` : returnTo;
    router.push(redirectUrl as string);
  };

  // Check if MFA is enabled for the user
  const checkMfaStatus = async () => {
    if (!user?.id || checkingMfa) return;

    setCheckingMfa(true);
    try {
      // Get MFA factors for the user
      const { data: factorsData } = await authService.mfaGetFactors();
      const totpFactors = factorsData?.totp || [];

      if (
        totpFactors.length > 0 &&
        totpFactors.some((factor) => factor.status === "verified")
      ) {
        // User has MFA enabled, start MFA challenge
        const verifiedFactor = totpFactors.find(
          (factor) => factor.status === "verified"
        );
        if (verifiedFactor) {
          try {
            // Generate a challenge
            const { data: challengeData } = await authService.mfaChallenge(
              verifiedFactor.id
            );

            if (!challengeData || !challengeData.id) {
              throw new Error("Failed to create MFA challenge");
            }

            // Set MFA challenge data and show dialog
            setMfaFactorId(verifiedFactor.id);
            setMfaChallengeId(challengeData.id);
            setShowMfaDialog(true);
            return;
          } catch (challengeError) {
            console.error("Error creating MFA challenge:", challengeError);
            // Continue with normal flow on error
            completeLoginFlow();
          }
        }
      } else {
        // No MFA required, proceed with login
        completeLoginFlow();
      }
    } catch (error) {
      console.error("Error checking MFA status:", error);
      // Continue with normal flow on error
      completeLoginFlow();
    } finally {
      setCheckingMfa(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;

    // Check if user is new by looking at created_at and last_sign_in_at
    // If they're within a few seconds of each other, it's likely a new user
    const createdAt = new Date(user.created_at).getTime();
    const lastSignIn = new Date(user.last_sign_in_at).getTime();
    const isNew = Math.abs(createdAt - lastSignIn) < 5000; // 5 seconds threshold
    setIsNewUser(isNew);

    let checkIntervalId: NodeJS.Timeout;

    const initialTimeoutId = setTimeout(() => {
      checkIntervalId = setInterval(() => {
        setAttempts((prev) => {
          const newAttempts = prev + 1;

          if (
            newAttempts >= MAX_ATTEMPTS ||
            sessionStorage.getItem(`user_registered_${user.id}`) === "true"
          ) {
            clearInterval(checkIntervalId);

            // Check MFA status after the initial registration/login process is complete
            checkMfaStatus();
          }

          return newAttempts;
        });
      }, CHECK_INTERVAL);
    }, INITIAL_WAIT);

    return () => {
      clearTimeout(initialTimeoutId);
      clearInterval(checkIntervalId);
    };
  }, [user?.id, router, returnTo, token]);

  const getWaitingMessage = () => {
    if (checkingMfa) return "Checking two-factor authentication status...";
    if (isNewUser === null) return "Checking account status...";

    if (isNewUser) {
      if (attempts === 0) return "Completing initial registration...";
      if (attempts < MAX_ATTEMPTS / 2) return "Almost there...";
      return "Just a few more moments...";
    } else {
      if (attempts === 0) return "Signing you in...";
      if (attempts < MAX_ATTEMPTS / 2) return "Almost there...";
      return "Finalizing login...";
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background">
      <div className="flex items-center gap-2">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
        <span className="text-muted-foreground">{getWaitingMessage()}</span>
      </div>

      {/* MFA Verification Dialog */}
      <MfaVerificationDialog
        open={showMfaDialog}
        onClose={() => {
          // Do nothing - keep the dialog open
          // This prevents dialog closing and the infinite redirect loop
          return;
        }}
        onSuccess={handleMfaSuccess}
        onError={handleMfaError}
        factorId={mfaFactorId}
        challengeId={mfaChallengeId}
      />
    </div>
  );
}
